/**
 * Defaults for design-system keys that were added after the original
 * `designSystemV2` shape was frozen.
 *
 * Existing stories don't have `displayFontSize`, `headingFontSize`, etc.
 * persisted, so reading directly from `story.settings.designSystemV2` leaves
 * the matching CSS root variables (`--display-size`, `--heading-size`, …)
 * undefined. The text element CSS already declares fallbacks, but root
 * variables also need to be present so the design-system editor previews
 * and the runtime computed-style lookups (e.g. `refresh.ts` font-size
 * detection) see the right values.
 *
 * Defaults are derived from the existing title/subtitle keys when possible,
 * with safe constants as a last resort.
 */

const TITLE_TO_DISPLAY_FONT_SCALE = 1.5;
const SUBTITLE_TO_HEADING_FONT_SCALE = 1.25;

/** Multiply a CSS length value (e.g. "40px") by a numeric factor. Falls back to the original string when the unit isn't px. */
const scalePxValue = (value: unknown, factor: number): string | undefined => {
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^(-?\d*\.?\d+)px$/);
  if (!match) return undefined;
  const scaled = Number.parseFloat(match[1]) * factor;
  if (Number.isNaN(scaled)) return undefined;
  return `${Math.round(scaled)}px`;
};

const pickFirstDefined = <T>(
  ...candidates: (T | undefined | null)[]
): T | undefined => {
  for (const c of candidates) {
    if (c !== undefined && c !== null && c !== "") return c;
  }
  return undefined;
};

/**
 * Returns a shallow-merged copy of `design` with derived defaults filled in
 * for the new display/heading keys whenever they're missing. Existing keys
 * are never overwritten.
 */
export const withDesignSystemDefaults = <T extends Record<string, unknown>>(
  design: T,
): T => {
  const out: Record<string, unknown> = { ...design };

  // ===== DISPLAY (h1) — derive from Title =====
  if (out.displayFontSize == null) {
    const fromTitle = scalePxValue(
      out.titleFontSize,
      TITLE_TO_DISPLAY_FONT_SCALE,
    );
    out.displayFontSize = fromTitle ?? "60px";
  }
  if (out.mobileDisplayFontSize == null) {
    const fromMobileTitle = scalePxValue(
      out.mobileTitleFontSize,
      TITLE_TO_DISPLAY_FONT_SCALE,
    );
    out.mobileDisplayFontSize = fromMobileTitle ?? "48px";
  }
  if (out.displayColor == null) {
    out.displayColor = pickFirstDefined(out.titleColor, "#121212");
  }
  if (out.displayFont == null) {
    out.displayFont = pickFirstDefined(out.titleFont, "Figtree");
  }
  if (out.displayStyle == null) {
    out.displayStyle = pickFirstDefined(out.titleStyle, "700");
  }
  if (out.displayTextTransform == null) {
    out.displayTextTransform = pickFirstDefined(out.titleTextTransform, "none");
  }

  // ===== HEADING (h3) — derive from Subtitle =====
  if (out.headingFontSize == null) {
    const fromSubtitle = scalePxValue(
      out.subtitleFontSize,
      SUBTITLE_TO_HEADING_FONT_SCALE,
    );
    out.headingFontSize = fromSubtitle ?? "30px";
  }
  if (out.mobileHeadingFontSize == null) {
    const fromMobileSubtitle = scalePxValue(
      out.mobileSubtitleFontSize,
      SUBTITLE_TO_HEADING_FONT_SCALE,
    );
    out.mobileHeadingFontSize = fromMobileSubtitle ?? "26px";
  }
  if (out.headingColor == null) {
    out.headingColor = pickFirstDefined(out.subtitleColor, "#121212");
  }
  if (out.headingFont == null) {
    out.headingFont = pickFirstDefined(out.subtitleFont, "Figtree");
  }
  if (out.headingStyle == null) {
    out.headingStyle = pickFirstDefined(out.subtitleStyle, "600");
  }
  if (out.headingTextTransform == null) {
    out.headingTextTransform = pickFirstDefined(
      out.subtitleTextTransform,
      "none",
    );
  }

  return out as T;
};
